/**
 * Interface switcher (ADR-0049) — hop between an app's exposed FRONT DOORS
 * (Web app · Chat · Voce) from within one app. This is DISTINCT from `AppSwitcher`
 * (which hops between different APPS the user is a member of).
 *
 * Self-hiding: an app exposes a surface only when its branding flags it — a web
 * home page (`home_slug`), a STANDALONE chat (`chat_standalone`) or a STANDALONE
 * voice (`voice_standalone`). With ≤1 surface there's nothing to switch to, so the
 * component renders NOTHING — safe to drop into any header unconditionally.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Globe, MessageSquare, Mic } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOutsideClick } from "@/hooks/useOutsideClick";
import { portalApi } from "@/lib/api-portal";
import { portalAssistantPath, portalHomePath } from "@/lib/portalAuth";

type Surface = "web" | "chat" | "voice";

const META: Record<Surface, { label: string; Icon: typeof Globe }> = {
  web: { label: "Web app", Icon: Globe },
  chat: { label: "Chat", Icon: MessageSquare },
  voice: { label: "Voce", Icon: Mic },
};

export function InterfaceSwitcher({
  appSlug,
  current,
}: {
  appSlug: string;
  current: Surface;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useOutsideClick(ref, () => setOpen(false), open);

  const brandingQ = useQuery({
    queryKey: ["portal-branding", appSlug],
    queryFn: () => portalApi.getBranding(appSlug),
    staleTime: 5 * 60_000,
  });
  const b = brandingQ.data;

  // The app's available front doors, in a stable order. `web` is available when
  // the app has a home page; chat/voce when the interface is STANDALONE (has its
  // own surface) — NOT the embedded-widget flag.
  const surfaces: { surface: Surface; to: string }[] = [
    b?.home_slug ? { surface: "web" as const, to: portalHomePath(appSlug) } : null,
    b?.chat_standalone
      ? { surface: "chat" as const, to: portalAssistantPath(appSlug, "chat") }
      : null,
    b?.voice_standalone
      ? { surface: "voice" as const, to: portalAssistantPath(appSlug, "voice") }
      : null,
  ].filter(Boolean) as { surface: Surface; to: string }[];

  // Nothing to switch to (a single front door) → render nothing.
  if (surfaces.length <= 1) return null;

  const Current = META[current].Icon;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Cambia interfaccia"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none"
      >
        <Current className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} />
        <span className="truncate">{META[current].label}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          className="portal-pop absolute left-0 z-50 mt-1 w-52 overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface text-foreground shadow-lg"
        >
          <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
            Interfacce
          </div>
          <div className="py-1">
            {surfaces.map(({ surface, to }) => {
              const { label, Icon } = META[surface];
              const active = surface === current;
              return (
                <button
                  key={surface}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    if (!active) navigate(to);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-elevated ${
                    active ? "font-medium text-accent" : "text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  {active && <Check className="h-4 w-4 shrink-0 text-accent" strokeWidth={2} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
